import { regionNodeKey, useRegionNodes, useRegions } from "@/hooks/useRegions";
import type { RegionNodeKind } from "@/api/regions.service";

const CHIP_STYLE = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 12,
  background: "#efe",
  color: "#242",
  fontSize: 12,
  border: "1px solid #cdc",
} as const;

export type RegionChipProps = {
  /** UUID of a `taxonomy.regions` node (firm-logos scoping / list filters). */
  regionId: string;
  /** Optional pre-resolved name (skips the regions hook lookup). */
  name?: string;
  className?: string;
};

/**
 * Render a `taxonomy.regions` (UUID) region as a small chip. Used by the
 * firm-logos surface and region list filters, which still operate on the UUID
 * admin taxonomy. Resolves the name from the shared `useRegions` cache.
 *
 * Profile region assignments now use `(kind,id)` — render those with
 * {@link RegionNodeChip} instead.
 */
export function RegionChip({ regionId, name, className }: RegionChipProps) {
  const { regions } = useRegions();
  const resolved =
    name ?? regions.find(r => r.id === regionId)?.name ?? `Region ${regionId.slice(0, 8)}`;

  return (
    <span
      data-testid="region-chip"
      data-region-id={regionId}
      className={className}
      style={CHIP_STYLE}
    >
      {resolved}
    </span>
  );
}

export type RegionNodeChipProps = {
  /**
   * An `api_region_country_v2` node reference `{ kind, id }`. Accepts both
   * `country` (profile publication targets — now country-only) and `region`
   * (still used by the region-scoped firm-logos surface), since this chip is a
   * shared name-resolver keyed by `${kind}:${id}`.
   */
  region: { kind: RegionNodeKind; id: number };
  className?: string;
};

/**
 * Render an `api_region_country_v2` node (`{ kind, id }`) as a chip. Resolves
 * the node name from the region-countries tree cache (`useRegionNodes`), keyed
 * by `${kind}:${id}`. Country targets resolve to a country name; region refs
 * (firm-logos) resolve to a region name.
 */
export function RegionNodeChip({ region, className }: RegionNodeChipProps) {
  const { nodes } = useRegionNodes();
  const key = regionNodeKey(region);
  const resolved = nodes.find(n => regionNodeKey(n) === key)?.name ?? `${region.kind} ${region.id}`;

  return (
    <span
      data-testid="region-chip"
      data-region-kind={region.kind}
      data-region-id={region.id}
      className={className}
      style={CHIP_STYLE}
    >
      {resolved}
    </span>
  );
}
